"use client";

import type { CSSProperties } from "react";

import { cn } from "@repo/blacksmith-ui/cn";
import { assetUrl } from "@/lib/assetUrl";

/**
 * Animated hammer icon for in-progress autofix indicators.
 *
 * The PNGs are APNG (Animated PNG) files – the browser plays the animation
 * natively, so no JS frame cycling is needed. Inline width/height/maxWidth
 * override Tailwind preflight (`img { max-width:100%; height:auto }`).
 *
 * Defaults to 16px so the autofix status slot matches every other pill
 * status icon; callers in tighter rows pass a smaller `size`.
 *
 * `inCircleBadge` nudges the glyph to optically center it inside the docked
 * pill's grey circle. The APNG canvas is 28×28, but the hammer artwork sits
 * ~2px right and ~5.5px low relative to center.
 */
export function AutofixHammerIcon({
  className,
  size = 16,
  inCircleBadge = false,
}: {
  className?: string;
  size?: number;
  inCircleBadge?: boolean;
}) {
  const imgStyle: CSSProperties = {
    width: size,
    height: size,
    maxWidth: "none",
  };

  return (
    <span
      className={cn(
        "relative shrink-0",
        inCircleBadge
          ? "block -translate-x-px -translate-y-px"
          : "inline-block",
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={assetUrl("autofix/hammer-light.png")}
        alt=""
        draggable={false}
        className="absolute inset-0 select-none dark:hidden"
        style={imgStyle}
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={assetUrl("autofix/hammer-dark.png")}
        alt=""
        draggable={false}
        className="absolute inset-0 hidden select-none dark:block"
        style={imgStyle}
      />
    </span>
  );
}
